import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Moment from 'moment';
import {
  AppDatePicker,
  AppFileUploader,
  AppRemixIcon,
  AppTextArea,
  AppDeleteModal,
  AppCheckbox,
  AppNoInfo,
} from '@/layout/AppComponents';
import ReactDOMServer from 'react-dom/server';
import styled from 'styled-components';
import AppInput from '../../../../components/form/AppInput';
import colors from '../../../../../constants/colors';
import minions from '../../../../../minions/minions';
import Translator from '../../../../components/helpers/Translator';
import { axiosPath } from '../../../../../routing/helpers/router';
import errorHandler from '@/layout/components/error/ErrorHandler';
import * as currentPageAction from '../../../../../store/actions/currentPageAction';
import useIsMounted from '../../../../components/hooks/useIsMounted';

export default function EvidenceEditForm({
  resourceObligationIndex,
  resourceObligationID,
  selectedPrevision,
  isLoading,
  onCancel,
  onRemove,
  onSave,
  elementID,
}) {
  const { moduleBridgeHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const view = useSelector((state) => state.currentPage?.view);
  const resLevel1 = useSelector(
    (state) => state.currentPage?.resourceObligationLevels?.level1
  );
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [error, setError] = useState();
  const [advancedChecklist, setAdvancedChecklist] = useState(false);
  const [loading, setLoading] = useState(true);
  const validationSchema = Yup.object().shape({
    name: Yup.string().required(
      ReactDOMServer.renderToString(
        <Translator file="user" name="user.required" />
      )
    ),
    date: Yup.string()
      .required(
        ReactDOMServer.renderToString(
          <Translator file="user" name="user.required" />
        )
      )
      .nullable(),
  });

  const toggleRemoveFile = (values, setFieldValue, fileName) => {
    const updateRemoveFiles = values.removeFiles;

    if (updateRemoveFiles.includes(fileName)) {
      setFieldValue(
        'removeFiles',
        updateRemoveFiles.filter((fn) => fn !== fileName)
      );
    } else {
      updateRemoveFiles.push(fileName);
      setFieldValue('removeFiles', updateRemoveFiles);
    }
  };

  const handleUpload = (values, setFieldValue, opt) => {
    const files = Array.from(opt).filter(
      (f) =>
        !values.files.includes(f.name) &&
        (!selectedPrevision?.evidence?.urls ||
          (selectedPrevision?.evidence?.urls &&
            !selectedPrevision?.evidence?.urls.includes(f.name)))
    );
    setFieldValue('files', [...values.files, ...files]);
  };

  const handleCancel = () => {
    if (isLoading) return;
    onCancel();
  };

  const loadAdvancedChecklist = useCallback(
    () =>
      axios
        .post(
          axiosPath(
            'api_r_user_module_resourceManager_resourceObligation_prevision_evidenceForm_get',
          ),
          {
            moduleBridgeHex,
            previsionID: selectedPrevision?.id,
          },
        )
        .then(({ data }) => {
          if (isMounted.current) {
            setAdvancedChecklist(data?.advancedChecklist);
            setLoading(false);
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, selectedPrevision],
  );

  useEffect(() => {
    if (selectedPrevision?.evidence?.advancedChecklist) {
      setLoading(false);
    } else {
      loadAdvancedChecklist();
    }
  }, []);

  const handleSave = useCallback(
    async (values) =>
      axios
        .post(
          axiosPath(
            "api_r_user_module_resourceManager_resourceObligation_prevision_evidence_set"
          ),
          minions.forms.toFormData({
            ...values,
            moduleBridgeHex,
            previsionID: selectedPrevision?.id,
            view,
          }),
          { headers: { "Content-Type": "multipart/form-data" } }
        )
        .then(() => {
          if (isMounted.current) {
            setError(undefined);
            dispatch(currentPageAction.change("updateCard", elementID, true));
            dispatch(
              currentPageAction.change(
                "updateResourceObligationTab",
                elementID,
                true
              )
            );
            onSave();
          }
        })
        .catch((e) => {
          if (e.response.status === 406) {
            setError(e.response.data);
          } else {
            dispatch(errorHandler(e));
          }
        }),
    [
      moduleBridgeHex,
      selectedPrevision,
      resourceObligationIndex,
      resourceObligationID,
      resLevel1,
      view,
    ]
  );

  const handleDelete = useCallback(
    async () =>
      axios
        .delete(
          axiosPath(
            "api_r_user_module_resourceManager_resourceObligation_prevision_evidence_delete"
          ),
          {
            data: {
              moduleBridgeHex,
              previsionID: selectedPrevision?.id,
              evidenceID: selectedPrevision?.evidence?.id,
            },
          }
        )
        .then(() => {
          dispatch(currentPageAction.change("updateCard", elementID));
          dispatch(
            currentPageAction.change("updateResourceObligationTab", elementID)
          );
          onRemove();
        })
        .catch((e) => dispatch(errorHandler(e))),
    [
      moduleBridgeHex,
      selectedPrevision,
      resourceObligationIndex,
      resourceObligationID,
      resLevel1,
    ]
  );

  if (loading) return <AppNoInfo minHeight={300} />;

  return (
    <EvidenceForm>
      <Formik
        initialValues={{
          name: selectedPrevision?.evidence?.name || '',
          description: selectedPrevision?.evidence?.description || '',
          removeFiles: [],
          files: [],
          updateStartDate: Moment(selectedPrevision?.startDate, 'DD/MM/YYYY')
            .startOf('day')
            .isAfter(Moment().startOf('day')),
          date:
            selectedPrevision?.evidence?.date || Moment().format('DD/MM/YYYY'),
          advancedChecklist:
            selectedPrevision?.evidence?.advancedChecklist || advancedChecklist,
        }}
        validationSchema={validationSchema}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={handleSave}
      >
        {({ handleSubmit, values, setFieldValue, errors }) => (
          <form onSubmit={handleSubmit}>
            <div className="container-fluid">
              <h3>Evidencia {selectedPrevision?.startDate}</h3>
              {error && (
                <div className="alert alert-danger mt-2 mb-2">{error}</div>
              )}
              <div className="row">
                <div
                  className={`col-${
                    values?.advancedChecklist !== undefined ? '6' : '12'
                  }`}
                >
                  <div className="row">
                    <div
                      className={`col-${
                        values?.advancedChecklist !== undefined ? '12' : '6'
                      }`}
                    >
                      <AppInput
                        title={
                          <Translator
                            file="module-legalCompliance"
                            name="name"
                          />
                        }
                        placeholder={
                          <Translator
                            file="module-legalCompliance"
                            name="name.description.compulsory"
                          />
                        }
                        icon={
                          <AppRemixIcon icon="double-quotes-l" noVariants />
                        }
                        value={values.name}
                        error={errors.name}
                        onChange={(opt) => setFieldValue('name', opt)}
                        isClearable
                      />
                      <AppDatePicker
                        title={
                          <Translator
                            file="module-legalCompliance"
                            name="date"
                          />
                        }
                        value={values.date}
                        error={errors.date}
                        onChange={(opt) => setFieldValue('date', opt)}
                        maxDate={Moment().toDate()}
                        showMonthDropdown
                        showYearDropdown
                        hasToggleType
                        required
                      />
                      <AppCheckbox
                        value={values.updateStartDate}
                        onChange={(v) => setFieldValue('updateStartDate', v)}
                        hasBorder
                        isDisabled={Moment(
                          selectedPrevision?.startDate,
                          'DD/MM/YYYY'
                        )
                          .startOf('day')
                          .isAfter(Moment().startOf('day'))}
                      >
                        <Translator
                          file="module-resourceManager"
                          name="resource.update.period.start.date.evidence"
                        />
                      </AppCheckbox>
                    </div>
                    <div
                      className={`col-${
                        values?.advancedChecklist !== undefined ? '12' : '6'
                      }`}
                    >
                      <AppTextArea
                        title={
                          <Translator
                            file="module-legalCompliance"
                            name="module.lc.evidence.description"
                          />
                        }
                        placeholder={
                          <Translator
                            file="module-legalCompliance"
                            name="name.description.compulsory"
                          />
                        }
                        icon={<AppRemixIcon icon="align-left" noVariants />}
                        onChange={(opt) => setFieldValue('description', opt)}
                        value={values.description}
                      />
                      <div
                        className="row"
                        style={{
                          overflowY: 'scroll',
                          maxHeight: '113px',
                        }}
                      >
                        {Array.isArray(selectedPrevision?.evidence?.urls) &&
                          selectedPrevision?.evidence?.urls?.length > 0 && (
                            <div className="col-lg-12">
                              <EvidenceFileList urls>
                                {selectedPrevision?.evidence?.urls.map(
                                  (fileName) => (
                                    <li
                                      key={fileName}
                                      title={
                                        values.removeFiles.includes(fileName)
                                          ? ReactDOMServer.renderToString(
                                              <Translator
                                                file="module-legalCompliance"
                                                name="click.restore"
                                              />
                                            )
                                          : ReactDOMServer.renderToString(
                                              <Translator
                                                file="module-legalCompliance"
                                                name="click.delete"
                                              />
                                            )
                                      }
                                      onClick={() =>
                                        toggleRemoveFile(
                                          values,
                                          setFieldValue,
                                          fileName
                                        )
                                      }
                                    >
                                      <AppRemixIcon
                                        className="pointer mr-3"
                                        icon="delete-bin"
                                        color={
                                          values.removeFiles.includes(fileName)
                                            ? colors.grey400
                                            : colors.danger
                                        }
                                      />
                                      {minions.files.fileIcon(
                                        fileName,
                                        true,
                                        values.removeFiles.includes(fileName)
                                          ? colors.grey400
                                          : null,
                                        'mr-2'
                                      )}
                                      <FileName
                                        isRemoved={values.removeFiles.includes(
                                          fileName
                                        )}
                                      >
                                        {fileName}
                                      </FileName>
                                    </li>
                                  )
                                )}
                              </EvidenceFileList>
                            </div>
                          )}
                        {values?.files && values?.files?.length > 0 && (
                          <>
                            {Array.isArray(selectedPrevision?.evidence?.urls) &&
                              selectedPrevision?.evidence?.urls?.length > 0 && (
                                <hr />
                              )}
                            <div className="col-lg-12">
                              <EvidenceFileList urls={false}>
                                {values?.files.map((f) => (
                                  <li
                                    key={f}
                                    title={ReactDOMServer.renderToString(
                                      <Translator
                                        file="module-legalCompliance"
                                        name="click.delete"
                                      />
                                    )}
                                    onClick={() =>
                                      setFieldValue(
                                        'files',
                                        values.files.filter(
                                          (file) => file !== f
                                        )
                                      )
                                    }
                                  >
                                    <AppRemixIcon
                                      className="pointer mr-3"
                                      icon="delete-bin"
                                      color={colors.danger}
                                    />
                                    {minions.files.fileIcon(
                                      f.name,
                                      true,
                                      null,
                                      'mr-2'
                                    )}
                                    {f?.name}
                                  </li>
                                ))}
                              </EvidenceFileList>
                            </div>
                          </>
                        )}
                      </div>
                      <AppFileUploader
                        isMulti
                        containerClassName="mt-0"
                        placeholder={
                          values.files.length === 0 ? (
                            <Translator
                              file="module-legalCompliance"
                              name="select.one.multiple.files"
                            />
                          ) : (
                            <Translator
                              file="general"
                              name="select.number.of.elements"
                              variables={{
                                elements: values.files.length,
                              }}
                            />
                          )
                        }
                        onChange={(opt) =>
                          handleUpload(values, setFieldValue, opt)
                        }
                        btnText={<Translator file="general" name="search..." />}
                      />
                    </div>
                  </div>
                </div>
                {values?.advancedChecklist !== undefined && (
                  <div className="col-6">
                    <h6>
                        <Translator
                            file="module-resourceManager"
                            name="recource.check-list"
                        />
                    </h6>
                    <ul className="p-0">
                      {values?.advancedChecklist.map((ac, i) => (
                        <AdvancedEvidenceElement
                          key={ac?.id}
                          $isDisabled={ac?.checked === undefined}
                        >
                          <AppCheckbox
                            value={ac?.checked}
                            onChange={(v) =>
                              setFieldValue(
                                `advancedChecklist[${i}].checked`,
                                v
                              )
                            }
                            containerClassName="mb-0 mt-0"
                          >
                            {ac?.name}
                          </AppCheckbox>
                          <AppRemixIcon
                            icon="close-circle"
                            className="ml-2 pointer"
                            title={
                              ac?.checked === undefined ? (
                                <Translator
                                  file="module-resourceManager"
                                  name="resource.activate"
                                />
                              ) : (
                                <Translator
                                  file="module-resourceManager"
                                  name="resource.deactivate"
                                />
                              )
                            }
                            onClick={() =>
                              setFieldValue(
                                `advancedChecklist[${i}].checked`,
                                ac?.checked === undefined ? false : undefined
                              )
                            }
                          />
                        </AdvancedEvidenceElement>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <div className="my-2">
                <span
                  className={`btn btn-xs btn-outline-light mr-2 ${
                    isLoading ? 'disabled' : ''
                  }`}
                  onClick={handleCancel}
                >
                  <Translator file="user" name="user.cancel" />
                </span>
                {selectedPrevision?.evidence && (
                  <span
                    className="btn btn-xs btn-outline-danger"
                    onClick={() => setShowDeleteModal(true)}
                  >
                    <Translator file="general" name="general.delete" />
                  </span>
                )}
                <div className="float-right">
                  <button
                    type="button"
                    className={`btn btn-xs btn-warning ${
                      isLoading ? 'disabled' : ''
                    } mr-2`}
                    onClick={() => {
                      setFieldValue('state', 2);
                      handleSubmit();
                    }}
                  >
                    <Translator
                      file="module-resourceManager"
                      name="resource.state2"
                    />
                  </button>
                  <button
                    type="button"
                    className={`btn btn-xs btn-success ${
                      isLoading ? 'disabled' : ''
                    }`}
                    onClick={() => {
                      setFieldValue('state', 1);
                      handleSubmit();
                    }}
                  >
                    <Translator
                      file="module-resourceManager"
                      name="resource.state1"
                    />
                  </button>
                </div>
              </div>
            </div>
          </form>
        )}
      </Formik>
      <AppDeleteModal
        show={showDeleteModal}
        title={
          <Translator
            file="module-legalCompliance"
            name="module.lc.remove-evidence-confirm-button"
          />
        }
        onConfirm={handleDelete}
        onClose={() => setShowDeleteModal(false)}
      />
    </EvidenceForm>
  );
}

const EvidenceForm = styled.div`
  h3 {
    font-size: 13px;
    font-weight: bold;
  }
`;
const EvidenceFileList = styled.ul`
  list-style: none;
  margin: 10px 0 0 0;
  padding-left: 6px;
  background-color: ${(props) => (props.urls ? colors.grey200 : 'initial')};
  li {
    cursor: pointer;
    display: flex;
    align-items: center;
    border-top: 1px solid ${colors.grey300};
    padding: 8px 0;
    span {
      font-size: 12px;
      color: ${colors.primary};
    }
  }
  li:first-child {
    border-top: 0;
  }
`;
const FileName = styled.span`
  text-decoration: ${(props) => (props.isRemoved ? 'line-through' : 'unset')};
`;

const AdvancedEvidenceElement = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 5px;
  color: ${(props) => (props.$isDisabled ? colors.grey500 : 'inherit')};
  & > div {
    text-decoration: ${(props) =>
      props.$isDisabled ? 'line-through' : 'unset'};
  }
`;
